'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { navItems } from './nav';

export default function MobileNav({ onDark = false }: { onDark?: boolean }) {
    const [isOpen, setIsOpen] = useState(false);
    const navRef = useRef<HTMLElement>(null);
    const pathname = usePathname();

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            // Don't close if clicking on the nav menu itself
            if (navRef.current && navRef.current.contains(event.target as Node)) {
                return;
            }
            
            // Don't close if clicking on the header (check if target is in header)
            const header = document.querySelector('header');
            if (header && header.contains(event.target as Node)) {
                return;
            }

            // Close menu for all other clicks
            setIsOpen(false);
        };

        // Add event listener with a small delay to avoid immediate dismissal
        const timeoutId = setTimeout(() => {
            document.addEventListener('click', handleClickOutside);
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`lg:hidden p-2 rounded-lg transition-colors ${
                    onDark ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10'
                }`}
                aria-label="Toggle menu"
            >
                <svg
                    className="w-6 h-6"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    {isOpen ? (
                        <path d="M6 18L18 6M6 6l12 12" />
                    ) : (
                        <path d="M4 6h16M4 12h16M4 18h16" />
                    )}
                </svg>
            </button>

            {isOpen && (
                <>
                    <div
                        className="fixed top-0 left-0 right-0 bottom-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
                        onClick={() => setIsOpen(false)}
                    />
                    <nav 
                        ref={navRef}
                        className="fixed top-3 left-3 right-3 sm:left-auto sm:right-4 sm:top-4 sm:max-w-sm rounded-2xl bg-[#0a1614]/85 backdrop-blur-2xl ring-1 ring-white/15 shadow-2xl z-[60] lg:hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="relative px-4 py-4 pr-[4.75rem] space-y-1">
                            {/* Close button - floating in top right */}
                            <button
                                onClick={() => setIsOpen(false)}
                                className="absolute top-4 right-4 p-2 rounded-lg text-white/80 hover:bg-white/10 transition-colors z-10"
                                aria-label="Close menu"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            {navItems.map((item) => {
                                const isActive = pathname === item.href;
                                return (
                                    <Link
                                        key={item.href}
                                        href={item.href}
                                        onClick={() => setIsOpen(false)}
                                        className={`relative block px-4 py-3 rounded-lg transition-colors ${
                                            isActive
                                                ? 'bg-white/10 text-[#7fd4cd] font-semibold'
                                                : 'text-white/85 hover:bg-white/10 hover:text-white font-medium'
                                        }`}
                                    >
                                        {item.label}
                                    </Link>
                                );
                            })}
                            <div className="pt-4 border-t border-white/15 space-y-2">
                                <Link
                                    href="/contact"
                                    onClick={() => setIsOpen(false)}
                                    className="block w-full text-center px-4 py-3 rounded-full bg-[#257676] text-white font-semibold hover:bg-[#1e5e5e] transition-colors"
                                >
                                    Get started
                                </Link>
                                <Link
                                    href="https://app.esci.io"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setIsOpen(false)}
                                    className="block w-full text-center px-4 py-3 rounded-full border border-white/30 text-white/90 font-semibold hover:border-white hover:text-white transition-colors"
                                >
                                    Log in
                                </Link>
                            </div>
                        </div>
                    </nav>
                </>
            )}
        </>
    );
}

